/**
 * useRoomPulse — the Dashboard's quiet whisper.
 *
 * Returns a single observational line for an event ledger row when
 * BOTH conditions are simultaneously true:
 *
 *   1. A kiosk has been redeemed within the last 30 minutes
 *      (booth pairing's `last_used_at` is fresh), AND
 *   2. A meaningful memory transition happened within the last
 *      15 minutes (any photo / memory-snap landed on the wall).
 *
 * Either signal alone is not enough — the night must feel alive,
 * not merely that a tablet is switched on. This is a *directional*
 * cue, never a live counter. The host reads it as an invitation
 * back into stewardship, not as a metric.
 *
 * The hook is constitutionally inert for events outside the living
 * states (gathering / returning) — it polls nothing and returns
 * null. This keeps the Dashboard quiet for the 95% of rows that
 * aren't tonight.
 *
 * No new endpoints; we reuse the three observational sources
 * already serving the Tonight ✦ tab.
 *
 * Banned vocabulary in the returned `phrase`: "Live", "Active",
 * "Online", "Connected", "Streaming", counts, timestamps, %.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { deriveEventState } from '../components/ModeTwoPrimitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIOSK_AWAKE_WINDOW_MS    = 30 * 60 * 1000;
const ROOM_GATHERING_WINDOW_MS = 15 * 60 * 1000;
const POLL_INTERVAL_MS         = 60 * 1000;

const LIVING_STATES = new Set(['gathering', 'returning']);

function parseTime(v) {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}
function latest(docs, field = 'created_at') {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  let best = null;
  for (const d of docs) {
    // Photos serialise `uploaded_at` while memory-snaps and
    // guestbook entries serialise `created_at`. Honour whichever
    // the document supplies so the AND-lock fires on either path.
    const t = parseTime(d?.[field]) || parseTime(d?.uploaded_at) || parseTime(d?.created_at);
    if (t != null && (best == null || t > best)) best = t;
  }
  return best;
}

/**
 * @returns {{ alive: boolean, phrase: string }|null}
 *   - null while event state is outside the living window
 *   - { alive: true,  phrase } when both conditions hold
 *   - { alive: false, phrase: '' } in living state but conditions not yet met
 *     (callers should render nothing in this case; we expose the shape
 *     so a future register can opt into a dormant whisper if desired)
 */
export default function useRoomPulse(event) {
  const [pulse, setPulse] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!event?.event_id) return undefined;

    const state = deriveEventState(event);
    if (!LIVING_STATES.has(state)) {
      // Not a living night — never poll, never whisper.
      setPulse(null);
      return undefined;
    }

    const fetchOnce = async () => {
      try {
        const [pairing, photos, snaps] = await Promise.allSettled([
          axios.get(`${API}/events/${event.event_id}/booth-pairing`),
          axios.get(`${API}/events/${event.event_id}/photos`),
          axios.get(`${API}/events/${event.event_id}/memory-snaps`),
        ]);
        if (!mountedRef.current) return;

        const pairingDoc = pairing.status === 'fulfilled' ? pairing.value.data : null;
        const photoList  = photos.status === 'fulfilled'
          ? (Array.isArray(photos.value.data) ? photos.value.data : (photos.value.data?.photos || []))
          : [];
        const snapList   = snaps.status === 'fulfilled'
          ? (Array.isArray(snaps.value.data) ? snaps.value.data : (snaps.value.data?.snaps || snaps.value.data?.memory_snaps || []))
          : [];

        const now = Date.now();
        const kioskLast = parseTime(pairingDoc?.last_used_at);
        const kioskAwake = !!(pairingDoc?.code && kioskLast && (now - kioskLast) < KIOSK_AWAKE_WINDOW_MS);

        const lastTransition = Math.max(latest(photoList) || 0, latest(snapList) || 0) || null;
        const recentTransition = !!(lastTransition && (now - lastTransition) < ROOM_GATHERING_WINDOW_MS);

        // Require BOTH — kiosk alone is not enough. This is the
        // user's constitutional lock: the *night itself* must be
        // alive, not just the tablet.
        const alive = kioskAwake && recentTransition;

        // Phrase selection — one line, directional toward Tonight ✦.
        // Priority is from "the night is itself" → "memories are
        // landing" so the more meaningful observation wins.
        let phrase = '';
        if (alive) {
          if (state === 'returning') {
            phrase = '✦ Memories are settling onto the wall';
          } else if (recentTransition && photoList.length + snapList.length >= 3) {
            phrase = '✦ The room is alive';
          } else {
            phrase = '✦ The room is gathering';
          }
        }

        setPulse({ alive, phrase });
      } catch (_) { /* silent — observational, not operational */ }
    };

    // Tick wrapper — skip when the tab is backgrounded. The hosted
    // browser still fires intervals while hidden but there's no
    // observer to read the pulse, so the network call is pure
    // waste. We resume on `visibilitychange` → visible with a
    // single fresh fetch so a returning host sees the room
    // immediately.
    const tickIfVisible = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchOnce();
    };

    fetchOnce();
    const id = setInterval(tickIfVisible, POLL_INTERVAL_MS);
    const onVis = () => {
      if (!document.hidden && mountedRef.current) fetchOnce();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [event?.event_id, event?.event_date, event?.payment_status]);

  return pulse;
}
